'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Phone, ArrowRight, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import Image from 'next/image';

import { setSession, getSession, AgentSession } from '@/lib/agent-auth';
import { logAudit } from '@/lib/audit';
import { supabase } from '@/lib/supabase';

export default function LoginPage() {
  const router = useRouter();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    const session = getSession();
    if (session) {
      router.replace('/home-screen');
    } else {
      setCheckingSession(false);
    }
  }, [router]);

  const formatPhoneForDisplay = (val: string) => {
    return val.replace(/\D/g, '').slice(0, 11);
  };

  const normalizePhone = (val: string) => {
    const digits = val.replace(/\D/g, '');
    if (digits.startsWith('234')) return `0${digits.slice(3)}`;
    if (digits.startsWith('0')) return digits;
    return `0${digits}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone || phone.length < 10) {
      setError('Please enter a valid phone number.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const normalized = normalizePhone(phone);

      // Use SECURITY DEFINER RPC function to bypass RLS for agent login
      // Direct table queries fail because user_profiles has RLS and anon has no auth.uid()
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_agent_by_phone', { p_phone: normalized });

      if (rpcError) {
        console.error('[AgentLogin] RPC error:', rpcError.message, rpcError.details, rpcError.hint);
        await logAudit('agent_login_failed', null, {
          phone: normalized,
          error: `RPC error: ${rpcError.message}`,
          details: rpcError.details,
        });
        setError(`Login failed: ${rpcError.message}`);
        setLoading(false);
        return;
      }

      const found = rpcData && rpcData.length > 0 ? rpcData[0] : null;

      if (!found) {
        // Try alternate phone formats before giving up
        // Some agents may be stored with +234 prefix
        const altPhone = normalized.startsWith('0')
          ? `+234${normalized.slice(1)}`
          : normalized;

        let altFound = null;
        if (altPhone !== normalized) {
          const { data: altData, error: altError } = await supabase
            .rpc('get_agent_by_phone', { p_phone: altPhone });

          if (!altError && altData && altData.length > 0) {
            altFound = altData[0];
          }
        }

        if (!altFound) {
          console.warn('[AgentLogin] Agent not found for phone:', normalized, '| alt:', altPhone);
          await logAudit('agent_login_failed', null, {
            phone: normalized,
            error: 'Phone not registered as active agent',
            normalized_phone: normalized,
            alt_phone: altPhone,
          });
          setError('This phone number is not registered as a MediQR Agent.');
          setLoading(false);
          return;
        }

        // Use the alt format match
        Object.assign(found || {}, altFound);
        const resolvedAgent = altFound;

        // Fetch union name if union_id exists
        let union_name: string | null = null;
        let union_logo_url: string | null = null;
        if (resolvedAgent.union_id) {
          const { data: unionData } = await supabase
            .from('md_unions')
            .select('union_name, logo_url')
            .eq('id', resolvedAgent.union_id)
            .single();
          if (unionData) {
            union_name = unionData.union_name;
            union_logo_url = unionData.logo_url;
          }
        }

        let state_name: string | null = null;
        if (resolvedAgent.state_id) {
          const { data: stateData } = await supabase
            .from('states')
            .select('state_name')
            .eq('id', resolvedAgent.state_id)
            .single();
          if (stateData) {
            state_name = stateData.state_name;
          }
        }

        const session: AgentSession = {
          id: resolvedAgent.id,
          full_name: resolvedAgent.full_name || '',
          first_name: resolvedAgent.first_name || '',
          last_name: resolvedAgent.last_name || '',
          phone: resolvedAgent.phone || altPhone,
          role: resolvedAgent.role as 'agent' | 'union_admin',
          user_status: resolvedAgent.user_status,
          union_id: resolvedAgent.union_id,
          union_name,
          union_logo_url,
          state_id: resolvedAgent.state_id,
          state_name,
          last_login_at: resolvedAgent.last_login_at,
          session_created_at: new Date().toISOString(),
        };

        setSession(session);
        await logAudit('agent_login', session, { phone: altPhone });
        setSuccess(true);
        setTimeout(() => router.push('/home-screen'), 800);
        return;
      }

      // Fetch union name if union_id exists
      let union_name: string | null = null;
      let union_logo_url: string | null = null;
      if (found.union_id) {
        const { data: unionData } = await supabase
          .from('md_unions')
          .select('union_name, logo_url')
          .eq('id', found.union_id)
          .single();
        if (unionData) {
          union_name = unionData.union_name;
          union_logo_url = unionData.logo_url;
        }
      }

      // Fetch state name if state_id exists
      let state_name: string | null = null;
      if (found.state_id) {
        const { data: stateData } = await supabase
          .from('states')
          .select('state_name')
          .eq('id', found.state_id)
          .single();
        if (stateData) {
          state_name = stateData.state_name;
        }
      }

      const session: AgentSession = {
        id: found.id,
        full_name: found.full_name || '',
        first_name: found.first_name || '',
        last_name: found.last_name || '',
        phone: found.phone || normalized,
        role: found.role as 'agent' | 'union_admin',
        user_status: found.user_status,
        union_id: found.union_id,
        union_name,
        union_logo_url,
        state_id: found.state_id,
        state_name,
        last_login_at: found.last_login_at,
        session_created_at: new Date().toISOString(),
      };

      setSession(session);
      await logAudit('agent_login', session, { phone: normalized });

      setSuccess(true);
      setTimeout(() => router.push('/home-screen'), 800);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[AgentLogin] Unexpected error:', message);
      setError(`Something went wrong: ${message}`);
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-dvh bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Image
            src="/assets/images/mediqr1logo-1785710782438.png"
            alt="MediQR"
            width={80}
            height={80}
            className="object-contain"
            priority
          />
          <Loader2 size={24} className="text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-gradient-to-b from-secondary to-white flex flex-col items-center justify-center px-6 py-12 safe-top">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-24 h-24 rounded-3xl bg-white flex items-center justify-center mb-4"
            style={{ boxShadow: '0 8px 32px rgba(14,165,233,0.18)' }}>
            <Image
              src="/assets/images/mediqr1logo-1785710782438.png"
              alt="MediQR"
              width={72}
              height={72}
              className="object-contain"
              priority
            />
          </div>
          <p className="text-base font-semibold text-primary tracking-wide">Agent Portal</p>
          <p className="text-sm text-muted-foreground mt-1">MediQR Field Operations</p>
        </div>

        {/* Form Card */}
        <div className="bg-card rounded-2xl p-6 border border-border"
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <h1 className="text-xl font-bold text-foreground mb-1">Sign In</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Enter your registered phone number to continue.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            {/* Phone input */}
            <div className="mb-4">
              <label
                htmlFor="phone"
                className="block text-sm font-600 text-foreground mb-2"
                style={{ fontWeight: 600 }}
              >
                Phone Number
              </label>
              <div className="flex gap-2">
                {/* Country code */}
                <div className="flex items-center gap-1.5 bg-input border border-border rounded-[10px] px-3 text-sm font-semibold text-foreground flex-shrink-0">
                  <span>🇳🇬</span>
                  <span>+234</span>
                </div>
                <input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="0801 234 5678"
                  value={phone}
                  onChange={(e) => {
                    setError('');
                    setPhone(formatPhoneForDisplay(e.target.value));
                  }}
                  className="input-base flex-1"
                  disabled={loading || success}
                  autoFocus
                  maxLength={11}
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <AlertCircle size={16} className="text-danger flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-danger font-medium">{error}</p>
                </div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || success || phone.length < 10}
            >
              {success ? (
                <>
                  <CheckCircle size={18} />
                  <span>Verified!</span>
                </>
              ) : loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Checking...</span>
                </>
              ) : (
                <>
                  <span>Continue</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          MediQR © 2026 · Agent PWA v1.0
        </p>
      </div>
    </div>
  );
}